import React, { useState } from 'react';
import { Card, CardContent, Typography, Box, Chip, useTheme } from '@mui/material';
import { styled } from '@mui/material/styles';
import { LoyaltyProgram } from '../../types';
import { getTierColor, getProgramTypeColor } from '../../theme';
import { getBrandLogo } from '../../services/brandLogos';
import CreditCardDisplay from './CreditCardDisplay';

const StyledCard = styled(Card)<{ programType: string }>(({ theme, programType }) => ({
  minHeight: 200,
  background: `linear-gradient(135deg, ${getProgramTypeColor(programType as any, theme.palette.mode)} 0%, ${getProgramTypeColor(programType as any, theme.palette.mode)}80 100%)`,
  color: 'white',
  position: 'relative',
  overflow: 'hidden',
  cursor: 'pointer',
  transition: 'all 0.3s cubic-bezier(0.4, 0, 0.2, 1)',
  backdropFilter: 'blur(20px)',
  border: theme.palette.mode === 'light' 
    ? '1px solid rgba(255, 255, 255, 0.2)' 
    : '1px solid rgba(255, 255, 255, 0.1)',
  '&:hover': {
    transform: 'translateY(-8px) scale(1.02)',
    boxShadow: theme.palette.mode === 'light' 
      ? '0 20px 40px rgba(0, 0, 0, 0.15)'
      : '0 20px 40px rgba(0, 0, 0, 0.4)',
  },
  '&::before': {
    content: '""',
    position: 'absolute',
    top: 0,
    right: 0,
    width: '120px',
    height: '120px',
    background: 'rgba(255, 255, 255, 0.08)',
    borderRadius: '50%',
    transform: 'translate(40px, -40px)',
  },
  '&::after': {
    content: '""',
    position: 'absolute',
    bottom: 0,
    left: 0,
    width: '80px',
    height: '80px',
    background: 'rgba(255, 255, 255, 0.05)',
    borderRadius: '50%',
    transform: 'translate(-20px, 20px)',
  },
}));

const LogoContainer = styled(Box)(({ theme }) => ({
  width: 60,
  height: 60,
  backgroundColor: 'rgba(255, 255, 255, 0.95)',
  borderRadius: 16,
  display: 'flex',
  alignItems: 'center',
  justifyContent: 'center',
  marginBottom: 16,
  boxShadow: '0 4px 12px rgba(0, 0, 0, 0.15)',
  overflow: 'hidden',
  position: 'relative',
  '& img': {
    width: '100%',
    height: '100%',
    objectFit: 'contain',
    transition: 'transform 0.3s ease',
  },
  '&:hover img': {
    transform: 'scale(1.05)',
  },
}));

const FallbackIcon = styled(Box)(({ theme }) => ({
  fontSize: '24px',
  display: 'flex',
  alignItems: 'center',
  justifyContent: 'center',
  width: '100%',
  height: '100%',
  background: 'linear-gradient(135deg, #667eea 0%, #764ba2 100%)',
  color: 'white',
  fontWeight: 600,
}));

const PointsDisplay = styled(Typography)({
  fontSize: '2rem',
  fontWeight: 700,
  lineHeight: 1,
  marginBottom: 4,
});

interface LoyaltyCardProps {
  program: LoyaltyProgram;
  onClick?: () => void;
  onDelete?: (program: LoyaltyProgram) => void;
  showDeleteButton?: boolean;
}

export const LoyaltyCard: React.FC<LoyaltyCardProps> = ({ program, onClick, onDelete, showDeleteButton = false }) => {
  const theme = useTheme();
  const [imageError, setImageError] = useState(false);
  const [imageLoading, setImageLoading] = useState(true);
  
  const formatPoints = (points: number): string => {
    if (points >= 1000000) return `${(points / 1000000).toFixed(1)}M`;
    if (points >= 1000) return `${(points / 1000).toFixed(1)}K`;
    return points.toLocaleString();
  };

  const getExpiryWarning = (): { text: string; color: string } | null => {
    if (!program.expiryDate) return null;
    
    const now = new Date();
    const expiry = new Date(program.expiryDate);
    const daysUntilExpiry = Math.ceil((expiry.getTime() - now.getTime()) / (1000 * 3600 * 24));
    
    if (daysUntilExpiry <= 30) {
      return {
        text: `Expires in ${daysUntilExpiry} days`,
        color: daysUntilExpiry <= 7 ? '#F56565' : '#ED8936'
      };
    }
    return null;
  };

  const handleImageLoad = () => {
    setImageLoading(false);
  };

  const handleImageError = () => {
    setImageError(true);
    setImageLoading(false);
  };

  const expiryWarning = getExpiryWarning();

  // Use CreditCardDisplay for credit cards
  if (program.type === 'credit_card') {
    const cardType = program.tier.toLowerCase().includes('platinum') ? 'platinum' 
                   : program.tier.toLowerCase().includes('gold') ? 'gold' : 'premium';
    
    return (
      <Box onClick={onClick}>
        <CreditCardDisplay 
          program={program} 
          cardType={cardType}
          onDelete={onDelete}
          showDeleteButton={showDeleteButton}
        >
          <Box sx={{ 
            display: 'flex', 
            flexDirection: 'column',
            gap: 0.4,
            minWidth: 0,
          }}>
            {/* Points Display */}
            <Box sx={{ 
              display: 'flex', 
              alignItems: 'baseline',
              gap: 0.5,
              mb: 0.3,
            }}>
              <Typography variant="body1" sx={{ 
                fontWeight: 700, 
                color: 'white',
                fontSize: '0.95rem',
                textShadow: '0 1px 3px rgba(0,0,0,0.8)',
                lineHeight: 1,
              }}>
                {formatPoints(program.points)}
              </Typography>
              <Typography variant="caption" sx={{ 
                color: 'rgba(255, 255, 255, 0.9)',
                fontSize: '0.65rem',
                textShadow: '0 1px 2px rgba(0,0,0,0.6)',
                textTransform: 'uppercase',
                fontWeight: 600,
              }}>
                {program.currency}
              </Typography>
            </Box>

            {/* Tier Badge */}
            <Chip 
              label={program.tier} 
              size="small"
              sx={{ 
                background: 'rgba(255, 255, 255, 0.15)', 
                backdropFilter: 'blur(4px)',
                color: 'white',
                fontWeight: 600,
                fontSize: '0.6rem',
                height: 16,
                width: 'fit-content',
                textShadow: '0 1px 2px rgba(0,0,0,0.6)',
                '& .MuiChip-label': {
                  px: 0.6,
                }
              }}
            />
            
            {/* Expiry Warning */}
            {expiryWarning && (
              <Typography 
                variant="caption" 
                sx={{ 
                  color: expiryWarning.color,
                  fontSize: '0.6rem',
                  fontWeight: 600,
                  background: 'rgba(0,0,0,0.5)',
                  padding: '1px 4px',
                  borderRadius: '3px',
                  display: 'inline-block',
                  textShadow: '0 1px 2px rgba(0,0,0,0.8)',
                  width: 'fit-content',
                  mt: 0.3,
                }}
              >
                {expiryWarning.text}
              </Typography>
            )}
          </Box>
        </CreditCardDisplay>
      </Box>
    );
  }

  return (
    <StyledCard programType={program.type} onClick={onClick}>
      <CardContent sx={{ p: 3, height: '100%', display: 'flex', flexDirection: 'column' }}>
        <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'flex-start', mb: 2 }}>
          <LogoContainer>
            {imageError || !program.logo ? (
              <FallbackIcon>
                {getBrandLogo(program.id).fallbackIcon}
              </FallbackIcon>
            ) : (
              <img 
                src={program.logo} 
                alt={program.name}
                onLoad={handleImageLoad}
                onError={handleImageError}
                style={{ 
                  opacity: imageLoading ? 0 : 1,
                  transition: 'opacity 0.3s ease'
                }}
              />
            )}
            {imageLoading && !imageError && (
              <Box
                sx={{
                  position: 'absolute',
                  top: 0,
                  left: 0,
                  right: 0,
                  bottom: 0,
                  display: 'flex',
                  alignItems: 'center',
                  justifyContent: 'center',
                  background: 'rgba(255, 255, 255, 0.9)',
                  borderRadius: 'inherit',
                }}
              >
                <Box
                  sx={{
                    width: 20,
                    height: 20,
                    border: '2px solid #f3f3f3',
                    borderTop: '2px solid #3498db',
                    borderRadius: '50%',
                    animation: 'spin 1s linear infinite',
                    '@keyframes spin': {
                      '0%': { transform: 'rotate(0deg)' },
                      '100%': { transform: 'rotate(360deg)' },
                    },
                  }}
                />
              </Box>
            )}
          </LogoContainer>
          <Chip
            label={program.tier}
            size="small"
            sx={{
              backgroundColor: getTierColor(program.tier, theme.palette.mode),
              color: 'white',
              fontWeight: 600,
              textShadow: '0 1px 2px rgba(0, 0, 0, 0.3)',
            }}
          />
        </Box>

        <Box sx={{ flex: 1 }}>
          <Typography variant="h6" sx={{ mb: 1, color: 'white', fontWeight: 600 }}>
            {program.name}
          </Typography>

          <PointsDisplay variant="h4">
            {formatPoints(program.points)}
          </PointsDisplay>
          
          <Typography variant="body2" sx={{ color: 'rgba(255, 255, 255, 0.8)', mb: 2 }}>
            {program.currency}
          </Typography>

          {expiryWarning && (
            <Chip
              label={expiryWarning.text}
              size="small"
              sx={{
                backgroundColor: expiryWarning.color,
                color: 'white',
                fontSize: '0.75rem',
              }}
            />
          )}
        </Box>

        <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', mt: 2 }}>
          <Typography variant="caption" sx={{ color: 'rgba(255, 255, 255, 0.7)' }}>
            {program.type.replace('_', ' ').toUpperCase()}
          </Typography>
          <Typography variant="body2" sx={{ color: 'white', fontWeight: 600 }}>
            View Details →
          </Typography>
        </Box>
      </CardContent>
    </StyledCard>
  );
};